import { useCallback, useEffect, useMemo, useRef, useState, type FormEvent, type ReactNode } from "react";
import { Badge } from "../ui/badge";
import { Button } from "../ui/button";
import { AsyncButton } from "../ui/async-button";
import { Checkbox } from "../ui/checkbox";
import { Input } from "../ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { Textarea } from "../ui/textarea";
import { Alert } from "../ui/alert";
import { Field } from "../ui/field";
import { cloudAuthDesktopV2, signInForCurrentRuntime } from "../../services/cloudAuthDesktopV2";
import { userFacingError } from "../../../shared/userFacingError";
import type { DesktopBetaApplication, DesktopBetaInvite } from "@repo/shared/desktopCloud/client";
import {
  formatCompensationSummary,
  type BetaReadCompensationScope,
  type BetaReadCompensationType,
  type BookBetaReadProgramConfig,
} from "@repo/shared/betaReadProgram";
import {
  createBetaQuestionOfKind,
  sortQuestionnaireQuestions,
  validateQuestionnaireQuestions,
  type BetaReaderQuestionnaireQuestion,
} from "@repo/shared/betaReaderQuestionnaire";
import { gatePlanFeature } from "../../../shared/planGate";
import { PanelHeader } from "./PanelHeader";
import { SectionCard } from "../ui/section-card";
import { EmptyState } from "../ui/empty-state";
import { Tabs, TabsContent, TabsCount, TabsList, TabsTrigger } from "../ui/tabs";

type BetaTab = "session" | "invites" | "signups" | "survey";

const TAB_META: Array<{ id: BetaTab; label: string; hint: string }> = [
  { id: "session", label: "Session", hint: "Window & compensation" },
  { id: "invites", label: "Invites", hint: "Email invites" },
  { id: "signups", label: "Sign-ups", hint: "Public applications" },
  { id: "survey", label: "End survey", hint: "Qualification questions" },
];

function isoToDatetimeLocal(iso: string | null): string {
  if (!iso) return "";
  const d = new Date(iso);
  if (!Number.isFinite(d.getTime())) return "";
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function datetimeLocalToIso(value: string): string | null {
  if (!value.trim()) return null;
  const d = new Date(value);
  return Number.isFinite(d.getTime()) ? d.toISOString() : null;
}

function formatSessionDate(iso: string | null): string | null {
  if (!iso) return null;
  const d = new Date(iso);
  if (!Number.isFinite(d.getTime())) return null;
  return d.toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" });
}

function formatShortDate(iso: string): string {
  const d = new Date(iso);
  if (!Number.isFinite(d.getTime())) return "";
  return d.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
}

function moveItem<T>(list: T[], fromIndex: number, toIndex: number): T[] {
  if (toIndex < 0 || toIndex >= list.length) return list;
  const next = [...list];
  const [item] = next.splice(fromIndex, 1);
  next.splice(toIndex, 0, item!);
  return next;
}

function BetaSection({
  title,
  description,
  children,
}: {
  title: string;
  description?: string;
  children: ReactNode;
}) {
  return (
    <SectionCard title={title} bodyClassName="flex flex-col gap-3.5">
      {description ? (
        <p className="max-w-[68ch] text-xs leading-relaxed text-muted-foreground">{description}</p>
      ) : null}
      {children}
    </SectionCard>
  );
}

function StatusBanner({
  kind,
  children,
}: {
  kind: "error" | "success" | "warn" | "info";
  children: ReactNode;
}) {
  const variant = {
    error: "destructive",
    success: "success",
    warn: "warning",
    info: "info",
  }[kind] as "destructive" | "success" | "warning" | "info";

  return (
    <Alert variant={variant} className="shrink-0 text-[0.8rem] leading-snug">
      {children}
    </Alert>
  );
}

function inviteStatusVariant(status: string): "default" | "secondary" | "destructive" | "outline" {
  if (status === "accepted") return "default";
  if (status === "revoked" || status === "expired") return "destructive";
  return "secondary";
}

function applicationStatusVariant(status: string): "default" | "secondary" | "destructive" | "outline" {
  if (status === "approved") return "default";
  if (status === "rejected") return "destructive";
  return "secondary";
}

type PanelData = {
  program: BookBetaReadProgramConfig | null;
  invites: DesktopBetaInvite[];
  applications: DesktopBetaApplication[];
  questions: BetaReaderQuestionnaireQuestion[];
  surveyVersion: number;
};

export function BetaReadersPanel({
  bookId,
  signedIn,
}: {
  bookId: string | null;
  signedIn: boolean;
}) {
  const [tab, setTab] = useState<BetaTab>("session");
  const [planNotice, setPlanNotice] = useState<string | null>(null);
  const [bootLoading, setBootLoading] = useState(false);
  const [bootError, setBootError] = useState<string | null>(null);
  const [data, setData] = useState<PanelData>({
    program: null,
    invites: [],
    applications: [],
    questions: [],
    surveyVersion: 0,
  });

  // Session draft (local edits; save explicitly)
  const [programDraft, setProgramDraft] = useState<BookBetaReadProgramConfig | null>(null);
  const [sessionSaving, setSessionSaving] = useState(false);
  const [sessionNotice, setSessionNotice] = useState<string | null>(null);
  const [sessionError, setSessionError] = useState<string | null>(null);

  // Invites
  const [inviteEmail, setInviteEmail] = useState("");
  const [inviteBusy, setInviteBusy] = useState(false);
  const [inviteError, setInviteError] = useState<string | null>(null);
  const [lastInvite, setLastInvite] = useState<{ inviteUrl: string; inviteCode: string } | null>(null);
  const [copied, setCopied] = useState<"code" | "url" | null>(null);

  // Sign-ups
  const [signupBusyId, setSignupBusyId] = useState<string | null>(null);
  const [signupError, setSignupError] = useState<string | null>(null);

  // Survey draft
  const [surveyDraft, setSurveyDraft] = useState<BetaReaderQuestionnaireQuestion[]>([]);
  const [surveySaving, setSurveySaving] = useState(false);
  const [surveyError, setSurveyError] = useState<string | null>(null);
  const [surveyNotice, setSurveyNotice] = useState<string | null>(null);

  const loadedBookId = useRef<string | null>(null);

  const refresh = useCallback(async (id: string, opts?: { soft?: boolean }) => {
    if (!opts?.soft) {
      setBootLoading(true);
      setBootError(null);
    }
    try {
      const [programRes, invites, applications, survey] = await Promise.all([
        cloudAuthDesktopV2.getBetaReadProgram(id),
        cloudAuthDesktopV2.listBetaInvites(id),
        cloudAuthDesktopV2.listBetaApplications(id),
        cloudAuthDesktopV2.getBetaEndSurvey(id),
      ]);
      const questions = sortQuestionnaireQuestions(survey.questions);
      setData({
        program: programRes.program,
        invites,
        applications,
        questions,
        surveyVersion: survey.version,
      });
      setProgramDraft(programRes.program);
      setSurveyDraft(questions);
      loadedBookId.current = id;
    } catch (err) {
      setBootError(userFacingError(err, "generic"));
    } finally {
      setBootLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!signedIn || !bookId) {
      loadedBookId.current = null;
      return;
    }
    if (loadedBookId.current === bookId) return;
    void refresh(bookId);
  }, [signedIn, bookId, refresh]);

  useEffect(() => {
    if (!signedIn) {
      setPlanNotice(null);
      return;
    }
    let cancelled = false;
    void cloudAuthDesktopV2
      .getBillingPlan()
      .then((billing) => {
        if (cancelled) return;
        const gate = gatePlanFeature(billing.plan, "betaRead", true);
        setPlanNotice(gate.ok ? null : gate.error);
      })
      .catch(() => {
        if (!cancelled) setPlanNotice(null);
      });
    return () => {
      cancelled = true;
    };
  }, [signedIn]);

  const pendingCount = useMemo(
    () => data.applications.filter((a) => a.status === "pending").length,
    [data.applications]
  );

  const programDirty = useMemo(() => {
    if (!programDraft || !data.program) return false;
    return JSON.stringify(programDraft) !== JSON.stringify(data.program);
  }, [programDraft, data.program]);

  const surveyDirty = useMemo(() => {
    return JSON.stringify(surveyDraft) !== JSON.stringify(data.questions);
  }, [surveyDraft, data.questions]);

  async function saveSession() {
    if (!bookId || !programDraft) return;
    setSessionSaving(true);
    setSessionError(null);
    setSessionNotice(null);
    try {
      const result = await cloudAuthDesktopV2.updateBetaReadProgram(bookId, programDraft);
      setData((prev) => ({ ...prev, program: result.program }));
      setProgramDraft(result.program);
      setSessionNotice("Session saved.");
    } catch (err) {
      setSessionError(userFacingError(err, "generic"));
    } finally {
      setSessionSaving(false);
    }
  }

  async function sendInvite(e: FormEvent) {
    e.preventDefault();
    if (!bookId) return;
    const trimmed = inviteEmail.trim();
    if (!trimmed) return;
    setInviteBusy(true);
    setInviteError(null);
    setLastInvite(null);
    setCopied(null);
    try {
      const result = await cloudAuthDesktopV2.createBetaInvite(bookId, trimmed);
      setLastInvite({ inviteUrl: result.inviteUrl, inviteCode: result.inviteCode });
      setInviteEmail("");
      const invites = await cloudAuthDesktopV2.listBetaInvites(bookId);
      setData((prev) => ({ ...prev, invites }));
    } catch (err) {
      setInviteError(userFacingError(err, "generic"));
    } finally {
      setInviteBusy(false);
    }
  }

  async function copyText(kind: "code" | "url", value: string) {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(kind);
      window.setTimeout(() => setCopied(null), 1600);
    } catch {
      /* ignore */
    }
  }

  async function reviewApplication(id: string, action: "approve" | "reject") {
    if (!bookId) return;
    setSignupBusyId(id);
    setSignupError(null);
    try {
      if (action === "approve") await cloudAuthDesktopV2.approveBetaApplication(bookId, id);
      else await cloudAuthDesktopV2.rejectBetaApplication(bookId, id);
      const applications = await cloudAuthDesktopV2.listBetaApplications(bookId);
      setData((prev) => ({ ...prev, applications }));
    } catch (err) {
      setSignupError(userFacingError(err, "generic"));
    } finally {
      setSignupBusyId(null);
    }
  }

  async function saveSurvey() {
    if (!bookId) return;
    setSurveySaving(true);
    setSurveyError(null);
    setSurveyNotice(null);
    try {
      const prepared = sortQuestionnaireQuestions(
        surveyDraft.map((q, order) => ({ ...q, order })).filter((q) => q.prompt.trim().length > 0)
      );
      const validation = validateQuestionnaireQuestions(prepared);
      if (!validation.ok) {
        setSurveyError(validation.error);
        return;
      }
      if (prepared.length === 0) {
        setSurveyError("Add at least one question.");
        return;
      }
      const survey = await cloudAuthDesktopV2.updateBetaEndSurvey(bookId, prepared);
      const questions = sortQuestionnaireQuestions(survey.questions);
      setData((prev) => ({ ...prev, questions, surveyVersion: survey.version }));
      setSurveyDraft(questions);
      setSurveyNotice("Survey saved.");
    } catch (err) {
      setSurveyError(userFacingError(err, "generic"));
    } finally {
      setSurveySaving(false);
    }
  }

  if (!signedIn) {
    return (
      <div
        className="flex h-full min-h-0 items-center justify-center bg-background p-8 text-center text-foreground"
        role="region"
        aria-label="Beta readers"
      >
        <EmptyState
          title="Sign in to manage beta readers"
          description="Connect your Just Write account to configure sessions, send invites, and review sign-ups."
          action={<Button onClick={() => void signInForCurrentRuntime()}>Sign in</Button>}
        />
      </div>
    );
  }

  if (!bookId) {
    return (
      <div
        className="flex h-full min-h-0 items-center justify-center bg-background p-8 text-center text-foreground"
        role="region"
        aria-label="Beta readers"
      >
        <EmptyState
          title="Open a book"
          description="Beta reader tools attach to a book in your account. Open one from your bookshelf, then come back here."
        />
      </div>
    );
  }

  const program = programDraft;
  const hasCompensation = program ? program.compensationType !== "none" : false;
  const previewSummary = program ? formatCompensationSummary(program) : null;

  return (
    <Tabs
      value={tab}
      onValueChange={(value) => setTab(value as BetaTab)}
      className="h-full min-h-0 gap-3.5 bg-background px-5 pt-4 pb-3 text-foreground"
      role="region"
      aria-label="Beta readers"
    >
      <PanelHeader
        title="Beta readers"
        description="Run a timed beta session, invite readers, approve sign-ups, and set the end-of-book survey."
        actions={
          <AsyncButton
            type="button"
            variant="outline"
            size="sm"
            busy={bootLoading}
            busyLabel="Refreshing…"
            onClick={() => void refresh(bookId, { soft: true })}
          >
            Refresh
          </AsyncButton>
        }
        className="border-b-0 px-0 py-0"
      />

      {planNotice ? <StatusBanner kind="warn">{planNotice}</StatusBanner> : null}
      {bootError ? <StatusBanner kind="error">{bootError}</StatusBanner> : null}

      <TabsList
        className="grid h-auto w-full shrink-0 grid-cols-2 gap-1.5 rounded-lg border border-border bg-muted/50 p-1.5 md:grid-cols-4"
        aria-label="Beta reader sections"
      >
        {TAB_META.map((item) => {
          return (
            <TabsTrigger
              key={item.id}
              value={item.id}
              className="h-auto min-w-0 flex-col items-start gap-0.5 px-2.5 py-2 text-left"
            >
              <span className="inline-flex items-center gap-1.5 text-[0.8125rem] font-semibold leading-tight">
                {item.label}
                {item.id === "signups" ? <TabsCount>{pendingCount}</TabsCount> : null}
              </span>
              <span className="block text-[0.6875rem] leading-snug opacity-75">{item.hint}</span>
            </TabsTrigger>
          );
        })}
      </TabsList>

      {bootLoading && !program ? (
        <div className="p-8 text-center text-sm text-muted-foreground">Loading beta reader data…</div>
      ) : (
        <div className="relative min-h-0 flex-1">
          {/* Session — keep mounted to avoid flash */}
          <TabsContent
            value="session"
            forceMount
            className="absolute inset-0 flex min-h-0 flex-col data-[state=inactive]:pointer-events-none data-[state=inactive]:invisible"
          >
            {!program ? (
              <EmptyState
                compact
                title="Session unavailable"
                description="Could not load the beta read program for this book."
              />
            ) : (
              <>
                <div className="flex min-h-0 max-w-4xl flex-1 flex-col gap-3.5 overflow-auto px-0.5 pt-0.5 pb-4">
                  <BetaSection
                    title="Access"
                    description="Control whether readers can apply from a public link."
                  >
                    <label className="flex cursor-pointer items-start gap-2.5" htmlFor="beta-enabled">
                      <Checkbox
                        id="beta-enabled"
                        checked={program.enabled}
                        onCheckedChange={(checked) =>
                          setProgramDraft({ ...program, enabled: checked === true })
                        }
                      />
                      <span>
                        <span className="block text-sm font-semibold">Open public signup</span>
                        <span className="mt-0.5 block text-xs leading-relaxed text-muted-foreground">
                          Readers can apply at your book’s /betaread link. You still approve each application.
                        </span>
                      </span>
                    </label>
                  </BetaSection>

                  <BetaSection title="Reader brief" description="Shown to readers when they join.">
                    <Field label="Instructions" htmlFor="beta-instructions">
                      <Textarea
                        id="beta-instructions"
                        rows={4}
                        value={program.instructions}
                        onChange={(e) =>
                          setProgramDraft({ ...program, instructions: e.target.value })
                        }
                        placeholder="What you expect, how to leave feedback, any hard deadlines…"
                      />
                    </Field>
                    <Field label="Incentives" hint="optional" htmlFor="beta-incentives">
                      <Textarea
                        id="beta-incentives"
                        rows={2}
                        value={program.incentives}
                        onChange={(e) =>
                          setProgramDraft({ ...program, incentives: e.target.value })
                        }
                        placeholder="Early access, acknowledgment, swag…"
                      />
                    </Field>
                  </BetaSection>

                  <BetaSection title="Compensation" description="What qualified readers earn.">
                    <div className="grid gap-3 sm:grid-cols-2">
                      <Field label="Type" htmlFor="beta-compensation-type">
                        <Select
                          value={program.compensationType}
                          onValueChange={(val) =>
                            setProgramDraft({
                              ...program,
                              compensationType: val as BetaReadCompensationType,
                            })
                          }
                        >
                          <SelectTrigger id="beta-compensation-type" className="w-full">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="none">None</SelectItem>
                            <SelectItem value="points">Points</SelectItem>
                            <SelectItem value="payment">Payment (Venmo)</SelectItem>
                          </SelectContent>
                        </Select>
                      </Field>
                      {hasCompensation ? (
                        <Field label="Scope" htmlFor="beta-compensation-scope">
                          <Select
                            value={program.compensationScope}
                            onValueChange={(val) =>
                              setProgramDraft({
                                ...program,
                                compensationScope: val as BetaReadCompensationScope,
                              })
                            }
                          >
                            <SelectTrigger id="beta-compensation-scope" className="w-full">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="book">Full book</SelectItem>
                              <SelectItem value="chapter">Per chapter</SelectItem>
                            </SelectContent>
                          </Select>
                        </Field>
                      ) : (
                        <div />
                      )}
                    </div>
                    {hasCompensation ? (
                      <Field
                        label={program.compensationType === "payment" ? "Amount (USD)" : "Amount (points)"}
                        htmlFor="beta-compensation-amount"
                        className="max-w-48"
                      >
                        <Input
                          id="beta-compensation-amount"
                          type="number"
                          min={0}
                          step={program.compensationType === "payment" ? 0.01 : 1}
                          value={
                            program.compensationType === "payment"
                              ? ((program.compensationAmount || 0) / 100).toFixed(2)
                              : program.compensationAmount || ""
                          }
                          onChange={(e) => {
                            const raw = parseFloat(e.target.value) || 0;
                            setProgramDraft({
                              ...program,
                              compensationAmount:
                                program.compensationType === "payment"
                                  ? Math.round(raw * 100)
                                  : Math.round(raw),
                            });
                          }}
                        />
                      </Field>
                    ) : null}
                    {previewSummary ? (
                      <p className="text-[0.78rem] leading-relaxed text-muted-foreground">
                        Readers will see: {previewSummary}
                      </p>
                    ) : null}
                  </BetaSection>

                  <BetaSection
                    title="Session window"
                    description="Optional start/stop. The stop date is the qualification deadline for points or payment."
                  >
                    <div className="grid gap-3 sm:grid-cols-2">
                      <Field label="Starts" htmlFor="beta-session-start">
                        <Input
                          id="beta-session-start"
                          type="datetime-local"
                          value={isoToDatetimeLocal(program.sessionStartsAt)}
                          onChange={(e) =>
                            setProgramDraft({
                              ...program,
                              sessionStartsAt: datetimeLocalToIso(e.target.value),
                            })
                          }
                        />
                      </Field>
                      <Field label="Stops" htmlFor="beta-session-stop">
                        <Input
                          id="beta-session-stop"
                          type="datetime-local"
                          value={isoToDatetimeLocal(program.sessionEndsAt)}
                          onChange={(e) =>
                            setProgramDraft({
                              ...program,
                              sessionEndsAt: datetimeLocalToIso(e.target.value),
                            })
                          }
                        />
                      </Field>
                    </div>
                    {program.sessionEndsAt || hasCompensation ? (
                      <p className="text-[0.78rem] leading-relaxed text-muted-foreground">
                        Readers must finish every chapter and the end-of-book survey by{" "}
                        <strong>
                          {formatSessionDate(program.sessionEndsAt) ?? "the session stop date"}
                        </strong>{" "}
                        to qualify.
                      </p>
                    ) : null}
                    <Field label="Reader cap" hint="optional" htmlFor="beta-max-readers" className="max-w-48">
                      <Input
                        id="beta-max-readers"
                        type="number"
                        min={1}
                        value={program.maxBetaReaders ?? ""}
                        onChange={(e) => {
                          const val = e.target.value;
                          setProgramDraft({
                            ...program,
                            maxBetaReaders: val === "" ? null : Math.max(1, parseInt(val, 10) || 1),
                          });
                        }}
                        placeholder="No limit"
                      />
                    </Field>
                  </BetaSection>

                  {sessionError ? <StatusBanner kind="error">{sessionError}</StatusBanner> : null}
                  {sessionNotice ? <StatusBanner kind="success">{sessionNotice}</StatusBanner> : null}
                </div>

                <footer className="flex max-w-4xl shrink-0 items-center justify-end gap-3 border-t border-border bg-background/90 px-0.5 pt-2.5 pb-1.5">
                  <span className="mr-auto text-xs text-muted-foreground">
                    {programDirty ? "Unsaved changes" : "All changes saved"}
                  </span>
                  <AsyncButton
                    type="button"
                    busy={sessionSaving}
                    busyLabel="Saving…"
                    disabled={!programDirty}
                    onClick={() => void saveSession()}
                  >
                    Save session
                  </AsyncButton>
                </footer>
              </>
            )}
          </TabsContent>

          {/* Invites */}
          <TabsContent
            value="invites"
            forceMount
            className="absolute inset-0 flex min-h-0 flex-col data-[state=inactive]:pointer-events-none data-[state=inactive]:invisible"
          >
            <div className="flex min-h-0 max-w-4xl flex-1 flex-col gap-3.5 overflow-auto px-0.5 pt-0.5 pb-4">
              <BetaSection title="Send an invite" description="Email must match the address the reader signs in with.">
                <form className="flex flex-wrap gap-2" onSubmit={(e) => void sendInvite(e)}>
                  <Input
                    type="email"
                    required
                    placeholder="reader@example.com"
                    value={inviteEmail}
                    onChange={(e) => setInviteEmail(e.target.value)}
                    disabled={inviteBusy}
                    aria-label="Beta reader email"
                    className="min-w-48 flex-1 basis-56"
                  />
                  <AsyncButton type="submit" busy={inviteBusy} busyLabel="Sending…" disabled={!inviteEmail.trim()}>
                    Send invite
                  </AsyncButton>
                </form>
                {inviteError ? <StatusBanner kind="error">{inviteError}</StatusBanner> : null}
                {lastInvite ? (
                  <div className="flex flex-col gap-2 rounded-lg border border-primary/30 bg-primary/5 p-3">
                    <p className="text-[0.8rem] font-semibold">
                      Invite created — copy the code now; it won’t be shown again.
                    </p>
                    <div className="flex min-w-0 items-center gap-2">
                      <span className="w-10 shrink-0 text-[0.7rem] font-semibold uppercase tracking-wide text-muted-foreground">
                        Code
                      </span>
                      <code className="min-w-0 flex-1 rounded-md border border-border bg-background px-2 py-1 font-mono text-xs">
                        {lastInvite.inviteCode}
                      </code>
                      <Button
                        type="button"
                        size="sm"
                        variant="outline"
                        onClick={() => void copyText("code", lastInvite.inviteCode)}
                      >
                        {copied === "code" ? "Copied" : "Copy"}
                      </Button>
                    </div>
                    <div className="flex min-w-0 items-center gap-2">
                      <span className="w-10 shrink-0 text-[0.7rem] font-semibold uppercase tracking-wide text-muted-foreground">
                        Link
                      </span>
                      <code className="min-w-0 flex-1 truncate rounded-md border border-border bg-background px-2 py-1 font-mono text-xs">
                        {lastInvite.inviteUrl}
                      </code>
                      <Button
                        type="button"
                        size="sm"
                        variant="outline"
                        onClick={() => void copyText("url", lastInvite.inviteUrl)}
                      >
                        {copied === "url" ? "Copied" : "Copy"}
                      </Button>
                    </div>
                  </div>
                ) : null}
              </BetaSection>

              <BetaSection title="Invite history">
                {data.invites.length === 0 ? (
                  <EmptyState
                    compact
                    title="No invites yet"
                    description="Send an email invite above. Pending invites appear here until accepted or expired."
                  />
                ) : (
                  <ul className="flex list-none flex-col gap-2">
                    {data.invites.map((inv) => (
                      <li key={inv.id} className="rounded-lg border border-border bg-card p-3">
                        <div className="flex flex-wrap items-center gap-x-2 gap-y-1.5">
                          <span className="text-sm font-semibold">{inv.inviteeEmail}</span>
                          <Badge variant={inviteStatusVariant(inv.status)}>{inv.status}</Badge>
                        </div>
                        <p className="mt-1 text-xs text-muted-foreground">
                          Invited {formatShortDate(inv.createdAt)}
                          {inv.acceptedAt ? ` · accepted ${formatShortDate(inv.acceptedAt)}` : ""}
                        </p>
                      </li>
                    ))}
                  </ul>
                )}
              </BetaSection>
            </div>
          </TabsContent>

          {/* Sign-ups */}
          <TabsContent
            value="signups"
            forceMount
            className="absolute inset-0 flex min-h-0 flex-col data-[state=inactive]:pointer-events-none data-[state=inactive]:invisible"
          >
            <div className="flex min-h-0 max-w-4xl flex-1 flex-col gap-3.5 overflow-auto px-0.5 pt-0.5 pb-4">
              <BetaSection
                title="Public applications"
                description={
                  pendingCount > 0
                    ? `${pendingCount} waiting for review.`
                    : "Readers who applied via your public beta link."
                }
              >
                {signupError ? <StatusBanner kind="error">{signupError}</StatusBanner> : null}
                {data.applications.length === 0 ? (
                  <EmptyState
                    compact
                    title="No applications yet"
                    description="When public signup is open, applications show up here for approve or reject."
                  />
                ) : (
                  <ul className="flex list-none flex-col gap-2">
                    {data.applications.map((row) => (
                      <li key={row.id} className="rounded-lg border border-border bg-card p-3">
                        <div className="flex flex-wrap items-center gap-x-2 gap-y-1.5">
                          <span className="text-sm font-semibold">
                            {row.applicantName ? `${row.applicantName} · ` : ""}
                            {row.applicantEmail}
                          </span>
                          <Badge variant={applicationStatusVariant(row.status)}>{row.status}</Badge>
                          {row.status === "pending" ? (
                            row.emailVerified ? (
                              <Badge variant="outline">Verified</Badge>
                            ) : (
                              <Badge variant="outline" className="border-amber-500/60 text-amber-700 dark:text-amber-400">
                                Unverified
                              </Badge>
                            )
                          ) : null}
                        </div>
                        {row.venmoHandle ? (
                          <p className="mt-1 text-xs text-muted-foreground">Venmo @{row.venmoHandle}</p>
                        ) : null}
                        <p className="mt-1 text-xs text-muted-foreground">
                          Applied {formatShortDate(row.createdAt)}
                        </p>
                        {row.status === "pending" ? (
                          <div className="mt-2 flex flex-wrap gap-1.5">
                            <Button
                              size="sm"
                              disabled={signupBusyId === row.id || !row.emailVerified}
                              title={
                                row.emailVerified
                                  ? undefined
                                  : "Applicant must verify email before approval"
                              }
                              onClick={() => void reviewApplication(row.id, "approve")}
                            >
                              Approve
                            </Button>
                            <Button
                              size="sm"
                              variant="ghost"
                              className="text-destructive hover:text-destructive"
                              disabled={signupBusyId === row.id}
                              onClick={() => void reviewApplication(row.id, "reject")}
                            >
                              Reject
                            </Button>
                          </div>
                        ) : null}
                      </li>
                    ))}
                  </ul>
                )}
              </BetaSection>
            </div>
          </TabsContent>

          {/* Survey */}
          <TabsContent
            value="survey"
            forceMount
            className="absolute inset-0 flex min-h-0 flex-col data-[state=inactive]:pointer-events-none data-[state=inactive]:invisible"
          >
            <div className="flex min-h-0 max-w-4xl flex-1 flex-col gap-3.5 overflow-auto px-0.5 pt-0.5 pb-4">
              <BetaSection
                title="End-of-book survey"
                description={`Required once for qualification. Template version ${data.surveyVersion || 1}.`}
              >
                {surveyError ? <StatusBanner kind="error">{surveyError}</StatusBanner> : null}
                {surveyNotice ? <StatusBanner kind="success">{surveyNotice}</StatusBanner> : null}
                <ol className="flex list-none flex-col gap-2">
                  {surveyDraft.map((q, idx) => (
                    <li key={q.id} className="flex flex-col gap-2 rounded-lg border border-border bg-card p-3">
                      <div className="flex items-center justify-between gap-2">
                        <span className="text-xs font-bold tracking-wide text-muted-foreground">Q{idx + 1}</span>
                        <div className="flex items-center gap-0.5">
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            disabled={idx === 0}
                            onClick={() => setSurveyDraft((prev) => moveItem(prev, idx, idx - 1))}
                          >
                            Up
                          </Button>
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            disabled={idx === surveyDraft.length - 1}
                            onClick={() => setSurveyDraft((prev) => moveItem(prev, idx, idx + 1))}
                          >
                            Down
                          </Button>
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            className="text-destructive hover:text-destructive"
                            onClick={() =>
                              setSurveyDraft((prev) => prev.filter((row) => row.id !== q.id))
                            }
                          >
                            Remove
                          </Button>
                        </div>
                      </div>
                      <Textarea
                        value={q.prompt}
                        rows={2}
                        placeholder={`Question ${idx + 1}`}
                        onChange={(e) => {
                          const v = e.target.value;
                          setSurveyDraft((prev) =>
                            prev.map((row) => (row.id === q.id ? { ...row, prompt: v } : row))
                          );
                        }}
                      />
                    </li>
                  ))}
                </ol>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() =>
                    setSurveyDraft((prev) => [
                      ...prev,
                      createBetaQuestionOfKind("paragraph", prev.length),
                    ])
                  }
                >
                  Add question
                </Button>
              </BetaSection>
            </div>
            <footer className="flex max-w-4xl shrink-0 items-center justify-end gap-3 border-t border-border bg-background/90 px-0.5 pt-2.5 pb-1.5">
              <span className="mr-auto text-xs text-muted-foreground">
                {surveyDirty ? "Unsaved changes" : "All changes saved"}
              </span>
              <AsyncButton
                type="button"
                busy={surveySaving}
                busyLabel="Saving…"
                disabled={!surveyDirty}
                onClick={() => void saveSurvey()}
              >
                Save survey
              </AsyncButton>
            </footer>
          </TabsContent>
        </div>
      )}
    </Tabs>
  );
}
